import { useLanguage } from "@/lib/i18n/LanguageContext";
import {
  MoreHorizontal,
  Download,
  Trash2,
  FolderInput,
  FilePlus2,
  Layers,
  Pencil,
  RotateCcw,
  Eye,
  BadgeCheck,
  MessageSquare,
  type LucideIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

interface DocumentActionsProps {
  /** Callback when download is clicked */
  onDownload: () => void;
  /** Callback when delete is clicked */
  onDelete: () => void;
  /** Disable all actions */
  disabled?: boolean;
  /** Show download action (default: true) */
  showDownload?: boolean;
  /** Show delete action (default: true) */
  showDelete?: boolean;
  /** Custom delete label (default: "Delete", or "Restore" when isRestore is true) */
  deleteLabel?: string;
  /** Render the destructive action as a non-destructive Restore action (icon + style + label).
   *  Used in the trash view where the menu item restores a soft-deleted document. */
  isRestore?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Whether this is a DWG file (gates the "View drawing" action) */
  isDwgFile?: boolean;
  /** Callback when "View drawing" is clicked (for parent DWG files). Opens the
   *  drawing viewer tool. No client-side permission gate — access is enforced
   *  server-side by the request_drawing_geometry RPC. */
  onOpenDrawingViewer?: () => void;
  onViewSignatures?: () => void;
  /** Callback when move/copy is clicked */
  onMoveCopy?: () => void;
  /** Show move/copy action (default: false) */
  showProjectActions?: boolean;
  /** Callback when "Add new version" is clicked. */
  onAddNewVersion?: () => void;
  /** The document already has versions: the item reads "Versioning". */
  opensVersioning?: boolean;
  /** Show "Add new version" action (default: true when callback provided) */
  showAddNewVersion?: boolean;
  /** Callback when Rename is clicked. Caller is responsible for permission gating
   *  the visibility (pass undefined to hide the menu item entirely). */
  onRename?: () => void;
  /** Callback when the Verified mark is toggled. Undefined hides the item —
   *  same permission gate as Rename. */
  onToggleVerified?: () => void;
  /** Current Verified state of this document; decides the label. */
  isVerified?: boolean;
  /** Open the comments panel on this document. Undefined hides the item. */
  onComments?: () => void;
  /** The hosting page's own action (label + icon), run on this document. */
  hostAction?: { label: string; icon: LucideIcon; onClick: () => void };
}

/**
 * DocumentActions - Molecule component
 *
 * Dropdown menu with document actions (Download, Delete).
 * Composes DropdownMenu with action items.
 */
export function DocumentActions({
  onDownload,
  onDelete,
  disabled = false,
  showDownload = true,
  showDelete = true,
  deleteLabel,
  isRestore = false,
  className,
  isDwgFile = false,
  onOpenDrawingViewer,
  onViewSignatures,
  onMoveCopy,
  showProjectActions = false,
  onAddNewVersion,
  opensVersioning = false,
  showAddNewVersion = true,
  onRename,
  onToggleVerified,
  isVerified = false,
  onComments,
  hostAction,
}: DocumentActionsProps) {
  const { t } = useLanguage();
  const resolvedDeleteLabel =
    deleteLabel ?? (isRestore ? t.documents.trash.restore : t.common.delete);
  const ActionIcon = isRestore ? RotateCcw : Trash2;
  const hasProjectActions = showProjectActions && onMoveCopy;
  const hasAddNewVersion = showAddNewVersion && !!onAddNewVersion;
  const hasRename = !!onRename;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          disabled={disabled}
          className={cn("h-8 w-8 p-0", className)}
        >
          <MoreHorizontal className="h-4 w-4" />
          <span className="sr-only">{t.documents.actions2.openMenu}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {onViewSignatures && (
          <DropdownMenuItem onClick={onViewSignatures}>
            <Eye className="mr-2 h-4 w-4" />
            {t.documents.signedContainer.panel.viewSignatures}
          </DropdownMenuItem>
        )}
        {showDownload && (
          <DropdownMenuItem onClick={onDownload}>
            <Download className="mr-2 h-4 w-4" />
            {t.documents.actions.download}
          </DropdownMenuItem>
        )}
        {isDwgFile && onOpenDrawingViewer && (
          <DropdownMenuItem onClick={onOpenDrawingViewer}>
            <Eye className="mr-2 h-4 w-4" />
            {t.documents.bulkActions.viewDrawing}
          </DropdownMenuItem>
        )}
        {hasAddNewVersion && (
          <DropdownMenuItem onClick={onAddNewVersion}>
            {opensVersioning ? (
              <Layers className="mr-2 h-4 w-4" />
            ) : (
              <FilePlus2 className="mr-2 h-4 w-4" />
            )}
            {opensVersioning
              ? t.documents.versioning.manage.menuItem
              : t.documents.versioning.addNewVersion.menuItem}
          </DropdownMenuItem>
        )}
        {hasProjectActions && (
          <DropdownMenuItem onClick={onMoveCopy}>
            <FolderInput className="mr-2 h-4 w-4" />
            {t.documents.actions2.moveCopyAction}
          </DropdownMenuItem>
        )}
        {hasRename && (
          <DropdownMenuItem onClick={onRename}>
            <Pencil className="mr-2 h-4 w-4" />
            {t.documents.actions2.rename}
          </DropdownMenuItem>
        )}
        {hostAction && (
          <DropdownMenuItem onClick={hostAction.onClick}>
            <hostAction.icon className="mr-2 h-4 w-4" />
            {hostAction.label}
          </DropdownMenuItem>
        )}
        {onComments && (
          <DropdownMenuItem onClick={onComments}>
            <MessageSquare className="mr-2 h-4 w-4" />
            {t.documents.bulkActions.comments}
          </DropdownMenuItem>
        )}
        {onToggleVerified && (
          <DropdownMenuItem onClick={onToggleVerified}>
            <BadgeCheck className="mr-2 h-4 w-4" />
            {isVerified ? t.documents.bulkActions.unverify : t.documents.bulkActions.verify}
          </DropdownMenuItem>
        )}
        {showDelete && (
          <DropdownMenuItem
            onClick={onDelete}
            className={cn(!isRestore && "text-destructive focus:text-destructive")}
          >
            <ActionIcon className="mr-2 h-4 w-4" />
            {resolvedDeleteLabel}
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
